import { cn, debounce } from "~/js/helpers.js";

const hasHover = (el) => el.matches(":hover");
const hasFocus = (el) => el.contains(document.activeElement);

export class Popover {
	#floatingEl;
	#open = false;
	#clickable = false;
	#hoverable = false;
	#defaultClass;
	#events;
	#resizeHandler;
	#hideTimer;

	constructor(referenceElement, options = {}) {
		this.options = {
			class: "",
			placement: "bottom",
			trigger: "hover",
			onShow: null,
			...options,
		};

		this.#defaultClass = cn(
			"border-stroke bg-foreground rounded-md border p-2 font-normal shadow-(--shadow-md)",
			this.options.class,
		);

		this.#clickable = this.options.trigger === "click";
		this.#hoverable = this.options.trigger === "hover";
		this.referenceEl = referenceElement;

		this.#floatingEl = document.createElement("div");
		this.#floatingEl.popover = "manual";
		this.#floatingEl.classList.add("popover-floating");

		this.referenceEl.insertAdjacentElement("afterend", this.#floatingEl);

		this.#events = [
			["focusin", this.#showHandler],
			["focusout", this.#hideHandler],
			["click", this.#showHandler],
			["keydown", this.#hideHandler],
			["mouseenter", this.#showHandler],
			["mouseleave", this.#hideHandler],
		];

		for (const element of [this.#floatingEl, this.referenceEl]) {
			if (element.tabIndex < 0) element.tabIndex = 0;
		}

		this.#addListeners();

		this.#resizeHandler = debounce(() => {
			if (this.#open) this.#updatePosition();
		}, 100);
		window.addEventListener("resize", this.#resizeHandler);
	}

	activate() {
		this.#clickable = this.options.trigger === "click";
		this.#hoverable = this.options.trigger === "hover";
		this.#addListeners();
		window.addEventListener("resize", this.#resizeHandler);
	}

	deactivate() {
		this.#clickable = false;
		this.#hoverable = false;
		this.#hide();
		this.#removeListeners();
		window.removeEventListener("resize", this.#resizeHandler);
	}

	setContent(content) {
		const wrapper = document.createElement("div");
		wrapper.className = this.#defaultClass;

		if (typeof content === "string") {
			wrapper.innerHTML = content;
		} else if (content?.innerHTML !== undefined) {
			wrapper.innerHTML = content.innerHTML;
		}

		this.#floatingEl.replaceChildren(wrapper);
	}

	#hide() {
		clearTimeout(this.#hideTimer);
		if (!this.#open) return;
		this.#open = false;
		try {
			this.#floatingEl.hidePopover();
		} catch {
			/* already hidden */
		}
	}

	#shouldHandle(eventName) {
		if (eventName === "focusin") return !this.#clickable;
		if (eventName === "focusout") return true;
		if (eventName === "click") return this.#clickable;
		if (eventName === "keydown") return this.#clickable;
		if (eventName === "mouseenter") return this.#hoverable;
		if (eventName === "mouseleave") return this.#hoverable;
		return false;
	}

	#addListeners() {
		for (const element of [this.#floatingEl, this.referenceEl]) {
			for (const [name, handler] of this.#events) {
				if (this.#shouldHandle(name)) element.addEventListener(name, handler);
			}
		}
	}

	#removeListeners() {
		for (const element of [this.#floatingEl, this.referenceEl]) {
			for (const [name, handler] of this.#events) {
				element.removeEventListener(name, handler);
			}
		}
	}

	#updatePosition() {
		const rect = this.referenceEl.getBoundingClientRect();
		const s = this.#floatingEl.style;
		const p = this.options.placement;

		this.#floatingEl.dataset.placement = p === "right" ? "right" : "bottom";

		if (p === "right") {
			s.left = `${rect.right + 8}px`;
			s.top = `${rect.top + rect.height / 2}px`;
			s.transform = "translateY(-50%)";
		} else if (p.endsWith("-end")) {
			s.top = `${rect.bottom + 8}px`;
			s.left = `${rect.right}px`;
			s.transform = "translateX(-100%)";
		} else if (p.endsWith("-start") || p.endsWith("-left")) {
			s.top = `${rect.bottom + 8}px`;
			s.left = `${rect.left}px`;
			s.transform = "";
		} else {
			// bottom, bottom-center (default)
			s.top = `${rect.bottom + 8}px`;
			s.left = `${rect.left + rect.width / 2}px`;
			s.transform = "translateX(-50%)";
		}
	}

	#showHandler = (ev) => {
		if (!this.#shouldHandle(ev.type)) return;
		if (!this.#floatingEl.firstChild) return;
		if (ev.relatedTarget === this.#floatingEl) return;

		// Toggle closed if clicking an already-open click trigger
		if (this.#open && this.#clickable && ev.target === this.referenceEl) {
			this.#hide();
			return;
		}
		if (this.#open) return;

		this.#open = true;
		this.#updatePosition();
		this.#floatingEl.showPopover();

		if (typeof this.options.onShow === "function") {
			this.options.onShow(this);
		}
	};

	#hideHandler = (ev) => {
		if (!this.#shouldHandle(ev.type)) return;
		clearTimeout(this.#hideTimer);
		this.#hideTimer = setTimeout(() => {
			const elements = [this.referenceEl, this.#floatingEl].filter(Boolean);
			if (ev.type === "mouseleave" && elements.some(hasHover)) return;
			if (ev.type === "focusout" && elements.some(hasFocus)) return;
			if (ev.type === "keydown" && ev.key !== "Escape" && this.#clickable) return;
			this.#hide();
		}, 100);
	};
}
